import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { forkJoin } from 'rxjs';
import {
  LineRichMenuConfig,
  LineRichMenuListItem,
} from '../../../../core/models/system-settings/line-rich-menu.model';
import { ApiLineRichMenuService } from '../../../../core/services/api/system-settings/api-line-rich-menu.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-line-rich-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './line-rich-menu.component.html',
  styleUrl: './line-rich-menu.component.css',
})
export class LineRichMenuComponent implements OnInit {
  private readonly api = inject(ApiLineRichMenuService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  isLoading = signal(false);
  isSaving = signal(false);
  isWorking = signal(false);

  config = signal<LineRichMenuConfig | null>(null);
  items = signal<LineRichMenuListItem[]>([]);

  memberRichMenuId = '';
  guestRichMenuId = '';
  createJson = '';
  uploadRichMenuId = '';
  testUid = '';
  testRichMenuId = '';
  userRichMenuId: string | null = null;
  userRichMenuMessage = '';

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  ngOnInit(): void {
    this.loadAll();
  }

  onRefresh(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.isLoading.set(true);
    forkJoin({
      config: this.api.getRichMenuConfig(),
      list: this.api.getRichMenus(),
    }).subscribe({
      next: ({ config, list }) => {
        this.config.set(config);
        this.memberRichMenuId = config.memberRichMenuId ?? '';
        this.guestRichMenuId = config.guestRichMenuId ?? '';
        this.items.set(list.items ?? []);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toast.error(err?.error?.error || err?.error?.message || 'โหลดข้อมูลไม่สำเร็จ');
      },
    });
  }

  onLoadTemplate(): void {
    this.api.getRichMenuCreateTemplate().subscribe({
      next: (res) => {
        this.createJson = JSON.stringify(res.template, null, 2);
        this.toast.success('โหลด template แล้ว — แก้ URI ก่อนสร้าง');
      },
      error: (err) => this.toast.error(err?.error?.message || 'โหลด template ไม่สำเร็จ'),
    });
  }

  onSaveConfig(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    const auth = this.resolveAuth();
    if (!auth) return;

    this.isSaving.set(true);
    this.api
      .saveRichMenuConfig({
        userId: auth.userId,
        menuId: auth.menuId,
        memberRichMenuId: this.memberRichMenuId.trim(),
        guestRichMenuId: this.guestRichMenuId.trim(),
      })
      .subscribe({
        next: (res) => {
          this.isSaving.set(false);
          if (!res?.isSuccess) {
            this.toast.error(res?.message || 'บันทึกไม่สำเร็จ');
            return;
          }
          this.toast.success(res.message || 'บันทึกแล้ว');
          this.loadAll();
        },
        error: (err) => {
          this.isSaving.set(false);
          this.toast.error(err?.error?.message || 'บันทึกไม่สำเร็จ');
        },
      });
  }

  onCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    const auth = this.resolveAuth();
    if (!auth) return;
    if (!this.createJson.trim()) {
      this.toast.error('กรุณาใส่ JSON ของ Rich Menu');
      return;
    }

    try {
      JSON.parse(this.createJson);
    } catch {
      this.toast.error('JSON ไม่ถูกต้อง');
      return;
    }

    this.isWorking.set(true);
    this.api
      .createRichMenu({
        userId: auth.userId,
        menuId: auth.menuId,
        richMenuJson: this.createJson,
      })
      .subscribe({
        next: (res) => {
          this.isWorking.set(false);
          if (!res?.success) {
            this.toast.error(res?.error || 'สร้างไม่สำเร็จ');
            return;
          }
          this.toast.success(`สร้างแล้ว: ${res.richMenuId}`);
          this.uploadRichMenuId = res.richMenuId || '';
          this.loadAll();
        },
        error: (err) => {
          this.isWorking.set(false);
          this.toast.error(err?.error?.error || err?.error?.message || 'สร้างไม่สำเร็จ');
        },
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์อัปโหลด');
      input.value = '';
      return;
    }
    if (!this.uploadRichMenuId.trim()) {
      this.toast.error('กรุณาเลือก / ใส่ richMenuId ก่อนอัปโหลด');
      input.value = '';
      return;
    }

    const auth = this.resolveAuth();
    if (!auth) {
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.isWorking.set(true);
      this.api
        .uploadRichMenuImage({
          userId: auth.userId,
          menuId: auth.menuId,
          richMenuId: this.uploadRichMenuId.trim(),
          fileName: file.name,
          contentType: file.type || 'image/png',
          base64: dataUrl,
        })
        .subscribe({
          next: (res) => {
            this.isWorking.set(false);
            input.value = '';
            if (!res?.success) {
              this.toast.error(res?.error || 'อัปโหลดไม่สำเร็จ');
              return;
            }
            this.toast.success('อัปโหลดรูปสำเร็จ');
          },
          error: (err) => {
            this.isWorking.set(false);
            input.value = '';
            this.toast.error(err?.error?.error || err?.error?.message || 'อัปโหลดไม่สำเร็จ');
          },
        });
    };
    reader.readAsDataURL(file);
  }

  useAsMember(item: LineRichMenuListItem): void {
    this.memberRichMenuId = item.richMenuId;
  }

  useAsGuest(item: LineRichMenuListItem): void {
    this.guestRichMenuId = item.richMenuId;
  }

  useForUpload(item: LineRichMenuListItem): void {
    this.uploadRichMenuId = item.richMenuId;
  }

  onDelete(item: LineRichMenuListItem): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    const auth = this.resolveAuth();
    if (!auth) return;
    if (!confirm(`ลบ Rich Menu ${item.richMenuId}?`)) return;

    this.isWorking.set(true);
    this.api
      .deleteRichMenu({
        userId: auth.userId,
        menuId: auth.menuId,
        richMenuId: item.richMenuId,
      })
      .subscribe({
        next: (res) => {
          this.isWorking.set(false);
          if (!res?.success) {
            this.toast.error(res?.error || 'ลบไม่สำเร็จ');
            return;
          }
          this.toast.success('ลบแล้ว');
          this.loadAll();
        },
        error: (err) => {
          this.isWorking.set(false);
          this.toast.error(err?.error?.error || err?.error?.message || 'ลบไม่สำเร็จ');
        },
      });
  }

  onLookupUser(): void {
    const uid = this.testUid.trim();
    if (!uid) {
      this.toast.error('ใส่ LINE uid');
      return;
    }
    this.api.getUserRichMenu(uid).subscribe({
      next: (res) => {
        this.userRichMenuId = res.richMenuId ?? null;
        this.userRichMenuMessage = res.message || '';
      },
      error: (err) => this.toast.error(err?.error?.error || err?.error?.message || 'ค้นหาไม่สำเร็จ'),
    });
  }

  onEnsureMember(): void {
    this.runUserAction('ensure');
  }

  onLink(): void {
    this.runUserAction('link');
  }

  onUnlink(): void {
    this.runUserAction('unlink');
  }

  private runUserAction(kind: 'ensure' | 'link' | 'unlink'): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์');
      return;
    }
    const auth = this.resolveAuth();
    if (!auth) return;
    const uid = this.testUid.trim();
    if (!uid) {
      this.toast.error('ใส่ LINE uid');
      return;
    }
    if (kind === 'link' && !this.testRichMenuId.trim()) {
      this.toast.error('ใส่ richMenuId สำหรับ link');
      return;
    }

    const payload = {
      userId: auth.userId,
      menuId: auth.menuId,
      lineUid: uid,
      richMenuId: this.testRichMenuId.trim() || null,
    };

    const req =
      kind === 'ensure'
        ? this.api.ensureMemberRichMenu(payload)
        : kind === 'link'
          ? this.api.linkRichMenu(payload)
          : this.api.unlinkRichMenu(payload);

    this.isWorking.set(true);
    req.subscribe({
      next: (res) => {
        this.isWorking.set(false);
        if (!res?.success) {
          this.toast.error(res?.error || 'ไม่สำเร็จ');
          return;
        }
        this.toast.success(res.message || 'สำเร็จ');
        this.onLookupUser();
      },
      error: (err) => {
        this.isWorking.set(false);
        this.toast.error(err?.error?.error || err?.error?.message || 'ไม่สำเร็จ');
      },
    });
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู — ตั้ง path_id 75 ในเมนู DB ก่อน');
      return null;
    }
    return { userId, menuId };
  }

  private resolveUserId(): number {
    const local = localStorage.getItem('userId');
    if (local) return Number(local);
    return Number(this.cookieService.get('user_id') || 0);
  }
}
